import {
  InputAdornment,
  Typography,
  TypographyVariant,
  useTheme,
} from '@mui/material'
import { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react'
import { MoneyDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import {
  DefinitiveDecimalTextField,
  DefinitiveDecimalTextFieldProps,
} from './DefinitiveDecimalTextField'

export type DefinitiveMoneyFieldProps = {
  moneyDto: MoneyDto
  onChange: (value: MoneyDto) => void
  validation?: (value: MoneyDto) => string[]
  onValidation?: (errors: string[]) => void
  showRawValueInTooltip?: (value: MoneyDto | '') => string
  typographyVariant?: TypographyVariant
  disabled?: boolean
} & Omit<
  DefinitiveDecimalTextFieldProps,
  'value' | 'onChange' | 'showRawValueInTooltip'
>

export const DefinitiveMoneyField = (props: DefinitiveMoneyFieldProps) => {
  const {
    moneyDto,
    validation: validationProp,
    onValidation: onValidationProp,
    onChange: onChangeProp,
  } = props

  const [currentMoney, setCurrentMoney] = useState(moneyDto)
  const [currentValidations, setCurrentValidations] =
    useState<string[]>(undefined)

  const theme = useTheme()

  const handleValidation = useCallback(
    (moneyDto: MoneyDto) => {
      const validations = validationProp ? validationProp(moneyDto) : undefined

      if (validations?.length > 0) {
        setCurrentValidations(validations)
      } else {
        setCurrentValidations(undefined)
      }

      if (validationProp && onValidationProp) {
        onValidationProp(validations)
      }

      return validations
    },
    [validationProp, onValidationProp],
  )

  const handleChange = useCallback(
    (field: keyof MoneyDto, value: number | string) => {
      const newQuantity = {
        ...currentMoney,
        [field]: value,
      }

      setCurrentMoney(newQuantity)
      handleValidation(newQuantity)
      onChangeProp(newQuantity)
    },
    [currentMoney, handleValidation, onChangeProp],
  )

  useEffect(() => {
    setCurrentMoney(props.moneyDto)
    handleValidation(props.moneyDto)
  }, [handleValidation, props.moneyDto])

  const htmlInputStyles: CSSProperties = useMemo(() => {
    return {
      textAlign: 'right',
      fontSize: theme.typography[props.typographyVariant || 'caption'].fontSize,
      padding: '.1em .1em',
      ...(props.textFieldProps?.slotProps?.htmlInput?.['style'] || {}),
    }
  }, [
    props.textFieldProps?.slotProps?.htmlInput,
    props.typographyVariant,
    theme.typography,
  ])

  if (!props.moneyDto || !currentMoney) {
    return null
  }

  return (
    <DefinitiveDecimalTextField
      {...props}
      numericFormatProps={{
        ...props.numericFormatProps,
        thousandSeparator: props.numericFormatProps?.thousandSeparator ?? true,
        decimalScale: props.numericFormatProps?.decimalScale ?? 2,
        fixedDecimalScale: true,
        allowNegative: props.numericFormatProps?.allowNegative ?? false,
      }}
      textFieldProps={{
        ...props.textFieldProps,
        slotProps: {
          ...props.textFieldProps?.slotProps,
          htmlInput: {
            ...props.textFieldProps?.slotProps?.htmlInput,
            style: htmlInputStyles,
          },
          input: {
            ...props.textFieldProps?.slotProps?.input,
            startAdornment: props.textFieldProps?.slotProps?.input?.[
              'startAdornment'
            ] ?? (
              <InputAdornment position="start">
                <Typography variant={props.typographyVariant || 'inherit'}>
                  {currentMoney.symbol}
                </Typography>
              </InputAdornment>
            ),
          },
        },
        disabled: props.disabled,
      }}
      maxValue={props.maxValue}
      minValue={props.minValue}
      value={currentMoney?.value}
      onChange={(value) => handleChange('value', value)}
      warnings={currentValidations}
      showRawValueInTooltip={
        props.showRawValueInTooltip &&
        ((value) =>
          props.showRawValueInTooltip({
            ...props.moneyDto,
            value: value?.toString() === '' ? undefined : 0,
          }))
      }
      isAllowed={props.isAllowed}
      disableWarningsIcon={props.disableWarningsIcon}
    />
  )
}
